// ระบบ 8 ภาษาของ Betner (แปลข้อความบนหน้าจอโดยไม่ต้องแก้ JSX ทีละจุด)
// - ข้อความต้นฉบับเป็นภาษาไทย -> หาในพจนานุกรม (dict1/dict2) แล้วแทนที่ตามภาษาที่เลือก
// - จำต้นฉบับไว้ สลับกลับเป็นไทยได้ / React เปลี่ยนข้อความใหม่ก็แปลให้อัตโนมัติ (MutationObserver)
// - ภาษาเก็บใน localStorage 'lang' (ใช้ key เดียวกับธีมอื่น)
import D1 from './dict1';
import D2 from './dict2';
import D3 from './dict3';

export const LANGS = [
  { code: 'th', short: 'TH', label: 'ไทย', flag: 'https://flagcdn.com/w40/th.png' },
  { code: 'en', short: 'EN', label: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
  { code: 'lo', short: 'LA', label: 'ລາວ', flag: 'https://flagcdn.com/w40/la.png' },
  { code: 'my', short: 'MM', label: 'မြန်မာ', flag: 'https://flagcdn.com/w40/mm.png' },
  { code: 'vi', short: 'VN', label: 'Tiếng Việt', flag: 'https://flagcdn.com/w40/vn.png' },
  { code: 'zh', short: 'CN', label: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
  { code: 'km', short: 'KH', label: 'ខ្មែរ', flag: 'https://flagcdn.com/w40/kh.png' },
  { code: 'ja', short: 'JP', label: '日本語', flag: 'https://flagcdn.com/w40/jp.png' },
];
const IDX = { en: 0, lo: 1, my: 2, vi: 3, zh: 4, km: 5, ja: 6 };
const STORAGE_KEY = 'lang';

const norm = (s) => String(s).replace(/ /g, ' ').replace(/\s+/g, ' ').trim();

// รวมพจนานุกรม (key ปรับช่องว่างแล้ว)
const DICT = new Map();
[D1, D2, D3].forEach((d) => Object.keys(d).forEach((k) => DICT.set(norm(k), d[k])));
// วลีสำหรับแทนบางส่วน (เช่น "ท่านติด Turnover อีก 500 บาท") เรียงยาว -> สั้น
const PHRASES = Array.from(DICT.keys()).filter((k) => k.length >= 1).sort((a, b) => b.length - a.length);
const TH_CHAR = /[\u0E01-\u0E3A\u0E40-\u0E5B]/; // อักษรไทย (ไม่นับ ฿)

function readSaved() {
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q && (q === 'th' || IDX[q] !== undefined)) return q;
    const s = window.localStorage.getItem(STORAGE_KEY);
    if (s && (s === 'th' || IDX[s] !== undefined)) return s;
  } catch (e) { /* ignore */ }
  return 'th';
}

let current = readSaved();
const listeners = new Set();
export const getLang = () => current;
export const subscribeLang = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

function lookup(th, lang) {
  const row = DICT.get(th);
  if (!row) return null;
  const v = row[IDX[lang]];
  return v === undefined || v === null || v === '' ? null : v;
}

// แปลข้อความ 1 ก้อน (คืน null ถ้าไม่มีอะไรต้องเปลี่ยน)
export function translateText(text, lang = current) {
  if (lang === 'th' || !text || !TH_CHAR.test(text)) return null;
  const key = norm(text);
  const exact = lookup(key, lang);
  if (exact !== null) {
    const lead = text.match(/^\s*/)[0];
    const tail = text.match(/\s*$/)[0];
    return lead + exact + tail;
  }
  // แทนบางส่วน: เฉพาะวลีที่ไม่ได้ติดกับตัวอักษรไทยอื่น (กันไปแปลชื่อห้อง/ชื่อเกมผิด เช่น "หวยรัฐบาลไทย")
  let out = text.replace(/ /g, ' ');
  let changed = false;
  for (const p of PHRASES) {
    if (out.indexOf(p) === -1) continue;
    const tr = lookup(p, lang);
    if (tr === null) continue;
    let from = 0;
    let idx;
    while ((idx = out.indexOf(p, from)) !== -1) {
      const before = out[idx - 1] || '';
      const after = out[idx + p.length] || '';
      if (!TH_CHAR.test(before) && !TH_CHAR.test(after)) {
        out = out.slice(0, idx) + tr + out.slice(idx + p.length);
        from = idx + tr.length;
        changed = true;
      } else {
        from = idx + p.length;
      }
    }
    if (!TH_CHAR.test(out)) break;
  }
  // แปลได้ครบทั้งก้อนเท่านั้น (กันข้อความไทยปนภาษาอื่นแบบครึ่ง ๆ กลาง ๆ)
  return changed && !TH_CHAR.test(out) ? out : null;
}

// ---------------- DOM ----------------
const ORIG = new WeakMap();      // text node -> ข้อความไทยต้นฉบับ
const LAST = new WeakMap();      // text node -> ข้อความที่เราใส่ล่าสุด
const ATTRS = ['placeholder', 'title', 'aria-label'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'NOSCRIPT']);

function skipEl(el) {
  for (let n = el; n && n !== document.body; n = n.parentNode) {
    if (n.nodeType === 1 && (SKIP_TAGS.has(n.tagName) || n.isContentEditable || (n.hasAttribute && n.hasAttribute('data-no-i18n')))) return true;
  }
  return false;
}

function doText(node) {
  const val = node.nodeValue;
  if (!val) return;
  // ถ้า React เปลี่ยนข้อความเอง (ไม่ใช่ค่าที่เราใส่) -> ถือเป็นต้นฉบับใหม่
  if (LAST.get(node) !== val) ORIG.set(node, val);
  const orig = ORIG.get(node);
  if (!orig || !TH_CHAR.test(orig)) return;
  if (node.parentNode && skipEl(node.parentNode)) return;
  const next = current === 'th' ? orig : (translateText(orig) ?? orig);
  if (next !== val) { LAST.set(node, next); node.nodeValue = next; } else { LAST.set(node, val); }
}

function doAttrs(el) {
  if (el.nodeType !== 1 || skipEl(el)) return;
  for (const a of ATTRS) {
    if (!el.hasAttribute(a)) continue;
    const okey = 'data-i18n-o-' + a;
    const lkey = 'data-i18n-l-' + a;
    const val = el.getAttribute(a);
    if (el.getAttribute(lkey) !== val) el.setAttribute(okey, val);
    const orig = el.getAttribute(okey);
    if (!orig || !TH_CHAR.test(orig)) continue;
    const next = current === 'th' ? orig : (translateText(orig) ?? orig);
    el.setAttribute(lkey, next);
    if (next !== val) el.setAttribute(a, next);
  }
  // ปุ่ม input type=button/submit
  if (el.tagName === 'INPUT' && /^(button|submit)$/i.test(el.type)) {
    const val = el.value;
    if (el.getAttribute('data-i18n-l-value') !== val) el.setAttribute('data-i18n-o-value', val);
    const orig = el.getAttribute('data-i18n-o-value');
    if (orig && TH_CHAR.test(orig)) {
      const next = current === 'th' ? orig : (translateText(orig) ?? orig);
      el.setAttribute('data-i18n-l-value', next);
      if (next !== val) el.value = next;
    }
  }
}

function walk(root) {
  if (!root) return;
  if (root.nodeType === 3) { doText(root); return; }
  if (root.nodeType !== 1 && root.nodeType !== 11) return;
  if (root.nodeType === 1) { if (SKIP_TAGS.has(root.tagName)) return; doAttrs(root); }
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === 3) doText(n); else doAttrs(n);
    n = tw.nextNode();
  }
}

let observer = null;
let pending = new Set();
let scheduled = false;
function flush() {
  scheduled = false;
  const list = Array.from(pending);
  pending = new Set();
  if (observer) observer.disconnect();
  list.forEach(walk);
  if (observer) observer.observe(document.body, OBS_OPTS);
}
const OBS_OPTS = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS.concat(['value']) };

export function startI18n() {
  if (observer || typeof MutationObserver === 'undefined') return;
  try { document.documentElement.lang = current; } catch (e) { /* ignore */ }
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'childList') m.addedNodes.forEach((n) => pending.add(n));
      else pending.add(m.target);
    }
    if (!scheduled) { scheduled = true; requestAnimationFrame(flush); }
  });
  const run = () => { walk(document.body); observer.observe(document.body, OBS_OPTS); };
  if (document.body) run(); else document.addEventListener('DOMContentLoaded', run);
}

export function setLang(code) {
  if (code !== 'th' && IDX[code] === undefined) return;
  if (code === current) return;
  current = code;
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* ignore */ }
  try { document.documentElement.lang = code; } catch (e) { /* ignore */ }
  if (observer) observer.disconnect();
  walk(document.body);
  if (observer) observer.observe(document.body, OBS_OPTS);
  listeners.forEach((fn) => fn(code));
}
